import {
  Building2,
  Cable,
  CalendarDays,
  FileAudio,
  Hash,
  Key,
  MessagesSquare,
  Monitor,
  Music,
  Phone,
  Settings,
  TrendingUpDown,
  UserRound,
  Users,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserState } from '~/atoms/UserState'
import { useCheckRole } from '~/hooks/useCheckRole'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { isMangoStaffOrSuperuser } from '~/utils/auth'

export type AdminMenuValue =
  | 'devices'
  | 'extensions'
  | 'switchboards'
  | 'schedules'
  | 'messaging-groups'
  | 'sound-clips'
  | 'music-on-hold'
  | 'phone-numbers'
  | 'locations'
  | 'system-settings'
  | 'pbx-settings'
  | 'users'
  | 'user-groups'
  | 'integrations'
  | 'api-access'

export type AdminMenuItem = {
  label: string
  value: AdminMenuValue
  icon: SVGComponent
  iconHeight?: number
}

export type AdminMenuGroupId = 'main' | 'developers'

export type AdminMenuGroup = {
  id: AdminMenuGroupId
  label: string | null
  items: AdminMenuItem[]
}

export const ADMIN_MENU_VALUES = new Set<string>([
  'devices',
  'extensions',
  'switchboards',
  'schedules',
  'messaging-groups',
  'sound-clips',
  'music-on-hold',
  'phone-numbers',
  'locations',
  'system-settings',
  'pbx-settings',
  'users',
  'user-groups',
  'integrations',
  'api-access',
])

// Values that belong to the "Developers" section. Everything else is in the
// main admin group. Kept as a Set so additions (e.g. future developer-focused
// items) only require one edit.
const DEVELOPER_MENU_VALUES = new Set<AdminMenuValue>(['api-access'])

export const useAdminMenuItems = () => {
  const { t } = useTranslation()
  const { userRoles } = useUserState()
  const checkRole = useCheckRole()
  const showTexting = useShowTexting()

  const isAdmin = checkRole('admin')
  const isSenior = checkRole('senior')
  const isMangoInternal = isMangoStaffOrSuperuser(userRoles)

  const { adminMenuItems, adminMenuGroups } = useMemo(() => {
    const allItems: (AdminMenuItem & { visible: boolean })[] = [
      {
        visible: isAdmin,
        label: t('nav.devices', 'Devices'),
        value: 'devices',
        icon: Monitor,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.extensions', 'Extensions'),
        value: 'extensions',
        icon: Phone,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.integrations', 'Integrations'),
        value: 'integrations',
        icon: Cable as SVGComponent,
        iconHeight: 20,
      },
      {
        visible: Boolean(isAdmin && showTexting),
        label: t('nav.messagingGroups', 'Messaging Groups'),
        value: 'messaging-groups',
        icon: MessagesSquare,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.musicOnHold', 'Music on Hold'),
        value: 'music-on-hold',
        icon: Music,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.phoneNumbers', 'Phone Numbers'),
        value: 'phone-numbers',
        icon: Hash as SVGComponent,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.locations', 'Locations'),
        value: 'locations',
        icon: Building2,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.schedules', t('schedule.list.title', 'Schedules')),
        value: 'schedules',
        icon: CalendarDays,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.soundClips', 'Sound Clips'),
        value: 'sound-clips',
        icon: FileAudio,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.switchboard', 'Switchboard'),
        value: 'switchboards',
        icon: TrendingUpDown,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.systemSettings', 'System Settings'),
        value: 'system-settings',
        icon: Settings,
        iconHeight: 20,
      },
      {
        visible: isMangoInternal,
        label: t('nav.pbxSettings', 'PBX Settings'),
        value: 'pbx-settings',
        icon: Settings,
        iconHeight: 20,
      },
      {
        visible: isSenior,
        label: t('nav.users', 'Users'),
        value: 'users',
        icon: UserRound,
        iconHeight: 20,
      },
      {
        visible: isSenior,
        label: t('nav.userGroups', 'User Groups'),
        value: 'user-groups',
        icon: Users,
        iconHeight: 20,
      },
      {
        visible: isAdmin,
        label: t('nav.apiAccess', 'API Access'),
        value: 'api-access',
        icon: Key as SVGComponent,
        iconHeight: 20,
      },
    ]

    const visibleItems = allItems
      .filter((item) => item.visible)
      .map(({ visible: _, ...item }) => item)

    const mainItems = visibleItems
      .filter((item) => !DEVELOPER_MENU_VALUES.has(item.value))
      .sort((a, b) => a.label.localeCompare(b.label))

    const developerItems = visibleItems
      .filter((item) => DEVELOPER_MENU_VALUES.has(item.value))
      .sort((a, b) => a.label.localeCompare(b.label))

    const groups: AdminMenuGroup[] = []
    if (mainItems.length > 0) {
      groups.push({ id: 'main', label: null, items: mainItems })
    }
    if (developerItems.length > 0) {
      groups.push({
        id: 'developers',
        label: t('nav.developers', 'Developers'),
        items: developerItems,
      })
    }

    return {
      adminMenuItems: [...mainItems, ...developerItems],
      adminMenuGroups: groups,
    }
  }, [isAdmin, isSenior, isMangoInternal, showTexting, t])

  return {
    adminMenuItems,
    adminMenuGroups,
    hasAdminItems: adminMenuItems.length > 0,
  }
}
